import { useEffect, useState } from 'react'
import { runwayDesignator, runwayWind } from '@jupi/shared'
import { CW_COLORS } from './CrosswindDiagram'

interface Props {
  runwayHdg: number
  windDir: number
  windSpeed: number
}

const W = 400
const H = 160
const MID_X = 200
const MID_Y = 106

const clampY = (v: number) => Math.max(78, Math.min(152, v))

// Ported from webplan-modern's features/crosswind/CockpitView.tsx. The
// arithmetic is the shared crosswind module's; this file only draws it.

/**
 * "View from the cockpit" letterbox: the pilot sits on the runway centreline
 * looking down the selected runway. Animated streamers show the combined
 * head/crosswind as it appears from the seat and the windsock matches the wind
 * direction. On a tailwind the streamers blow away toward the horizon, a
 * Garmin-style red X crosses the sky, and after 10 s the grim reaper walks past.
 */
export function CockpitView({ runwayHdg, windDir, windSpeed }: Props) {
  const wc = runwayWind(runwayHdg, windDir, windSpeed)
  const isTailwind = wc.headwindKt < 0
  const des = runwayDesignator(runwayHdg)

  // The grim reaper appears 10 s after the runway turns unusable (tailwind).
  const [reaper, setReaper] = useState(false)
  useEffect(() => {
    if (!isTailwind) {
      setReaper(false)
      return
    }
    const id = window.setTimeout(() => setReaper(true), 10_000)
    return () => window.clearTimeout(id)
  }, [isTailwind])

  // Downwind direction relative to the runway heading, projected into the seat's 2-D view.
  // ux: +right / -left across the view.  uy: +toward the pilot / -toward the horizon.
  const relRad = (((windDir + 180 - runwayHdg) % 360) * Math.PI) / 180
  const ux = Math.sin(relRad)
  const uy = -Math.cos(relRad)
  const fy = uy * 0.55 // foreshorten the along-runway axis

  const showWind = windSpeed > 0.5
  const runLen = Math.min(96, 22 + windSpeed * 3.4)

  // windsock: blends from hanging straight down (calm) to the wind direction (strong)
  const windAngle = (Math.atan2(fy, ux) * 180) / Math.PI
  const strength = Math.min(1, windSpeed / 18)
  const sockAngle = 90 * (1 - strength) + windAngle * strength
  const sockLen = (18 + windSpeed * 1.0) * (0.45 + 0.55 * Math.hypot(ux, fy))
  const sockX = ux <= 0 ? W - 48 : 48
  const sockPoleTop = 60

  const DUR = Math.max(1.1, 2.4 - windSpeed * 0.04)

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      role="img"
      aria-label={`Cockpit view down runway ${des}. ${
        isTailwind
          ? 'Tailwind — runway not usable.'
          : `Headwind ${wc.headwindKt.toFixed(0)} kt, crosswind ${Math.abs(wc.crosswindKt).toFixed(0)} kt.`
      }`}
      style={{
        display: 'block',
        maxWidth: 460,
        margin: '8px auto 0',
        borderRadius: 6,
      }}
    >
      {/* sky + ground */}
      <rect x={0} y={0} width={W} height={74} fill="#bfe0f2" />
      <rect x={0} y={74} width={W} height={H - 74} fill="#7fa06f" />
      <line x1={0} y1={74} x2={W} y2={74} stroke="#5f7d52" strokeWidth={1} />

      {/* runway in perspective */}
      <polygon points={`104,${H} 296,${H} 208,76 192,76`} fill="#4b5157" />
      {(
        [
          [200, 111, 3.0],
          [200, 101, 2.4],
          [200, 93, 1.8],
          [200, 86, 1.3],
          [200, 80, 1.0],
        ] as [number, number, number][]
      ).map(([cx, cy, hw], i) => (
        <rect key={i} x={cx - hw} y={cy} width={hw * 2} height={i < 2 ? 7 : 5} fill="#e9e9e9" />
      ))}
      {[-34, -20.5, -7, 7, 20.5, 34].map((dx) => (
        <rect key={dx} x={200 + dx - 3} y={H - 12} width={6} height={12} fill="#e9e9e9" />
      ))}
      <text
        x={200}
        y={H - 20}
        fontSize={20}
        fontWeight={800}
        fill="#e9e9e9"
        textAnchor="middle"
        style={{ letterSpacing: 3 }}
      >
        {des}
      </text>

      {/* combined head + crosswind streamers, as the air moves past the seat */}
      {showWind &&
        [0, -DUR / 3, -(2 * DUR) / 3].map((delay, i) => {
          const sx = MID_X - ux * runLen * 0.5
          const sy = clampY(MID_Y - uy * runLen * 0.5)
          const ex = MID_X + ux * runLen * 0.5
          const ey = clampY(MID_Y + uy * runLen * 0.5)
          return (
            <g key={i}>
              <animateMotion
                dur={`${DUR}s`}
                repeatCount="indefinite"
                rotate="auto"
                begin={`${delay}s`}
                path={`M ${sx},${sy} L ${ex},${ey}`}
              />
              <g>
                <animateTransform
                  attributeName="transform"
                  type="scale"
                  values="0.6;1.35"
                  dur={`${DUR}s`}
                  repeatCount="indefinite"
                  begin={`${delay}s`}
                />
                <polyline
                  points="-8,-6 0,0 -8,6"
                  fill="none"
                  stroke={CW_COLORS.wind}
                  strokeWidth={2.6}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <animate
                    attributeName="opacity"
                    values="0;1;1;0"
                    keyTimes="0;0.2;0.72;1"
                    dur={`${DUR}s`}
                    repeatCount="indefinite"
                    begin={`${delay}s`}
                  />
                </polyline>
                <polyline
                  points="-17,-6 -9,0 -17,6"
                  fill="none"
                  stroke={CW_COLORS.wind}
                  strokeWidth={2.6}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <animate
                    attributeName="opacity"
                    values="0;1;1;0"
                    keyTimes="0;0.2;0.72;1"
                    dur={`${DUR}s`}
                    repeatCount="indefinite"
                    begin={`${delay}s`}
                  />
                </polyline>
              </g>
            </g>
          )
        })}

      {/* windsock */}
      <g transform={`translate(${sockX} ${sockPoleTop})`}>
        <line x1={0} y1={0} x2={0} y2={54} stroke="#6b6b6b" strokeWidth={3} />
        <g>
          <animateTransform
            attributeName="transform"
            type="rotate"
            values={`${sockAngle - 5};${sockAngle + 5};${sockAngle - 5}`}
            dur="1.3s"
            repeatCount="indefinite"
          />
          <path d={`M0,-5 L${sockLen},-3.2 L${sockLen},3.2 L0,5 Z`} fill="#f5a623" />
          <line x1={sockLen * 0.38} y1={-4.4} x2={sockLen * 0.38} y2={4.4} stroke="#fff" strokeWidth={2.4} />
          <line x1={sockLen * 0.66} y1={-3.8} x2={sockLen * 0.66} y2={3.8} stroke="#fff" strokeWidth={2.4} />
        </g>
      </g>

      {/* tailwind → Garmin-style red X across the sky */}
      {isTailwind && (
        <g stroke="#e01b1b" strokeWidth={2.2} strokeLinecap="round">
          <line x1={2} y1={2} x2={W - 2} y2={72} />
          <line x1={W - 2} y1={2} x2={2} y2={72} />
        </g>
      )}

      {/* tailwind → the grim reaper walks past, 10 s after the runway turns unusable */}
      {reaper && (
        <>
          <style>
            {'@keyframes cwReaperWalk{0%,4%{transform:translate(-70px,120px)}60%,100%{transform:translate(470px,120px)}}' +
              '.cwReaper{animation:cwReaperWalk 15s linear infinite}'}
          </style>
          <g className="cwReaper">
            {/* hooded cloak */}
            <path
              d="M0,-50 C7,-50 9,-42 8,-35 C12,-31 13,-16 15,2 C8,-1 -8,-1 -15,2 C-13,-16 -12,-31 -8,-35 C-9,-42 -7,-50 0,-50 Z"
              fill="#0c0c0c"
            />
            {/* shadowed face */}
            <ellipse cx={0} cy={-39} rx={3.4} ry={5} fill="#333" />
            {/* scythe */}
            <line x1={9} y1={-24} x2={25} y2={-56} stroke="#6b533a" strokeWidth={2.2} strokeLinecap="round" />
            <path d="M25,-56 C36,-55 41,-47 33,-43 C37,-49 31,-55 25,-56 Z" fill="#d3d8dc" />
          </g>
        </>
      )}
    </svg>
  )
}
